import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, MapPin, Users, Clock, Plus, Music, Trash2, Image, Upload, X, Check, Ticket, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';

// EventCard component moved outside of Events
const EventCard = ({ event, showRSVP = true, user, isAdmin, onDelete, onAttend }) => {
  const isAttending = event.attendees?.includes(user?.user_id);
  const isFull = event.max_attendees && event.attendees?.length >= event.max_attendees;
  const eventDate = new Date(event.event_date + 'T' + event.event_time);
  
  // Get flyer image URL
  const getFlyerUrl = () => {
    if (!event.flyer_url) return null;
    if (event.flyer_url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${event.flyer_url}`;
    }
    return event.flyer_url;
  };
  const flyerUrl = getFlyerUrl();

  return (
    <div className="bg-white/5 rounded-xl overflow-hidden hover:bg-white/10 transition-colors">
      {/* Flyer Image */}
      {flyerUrl && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={flyerUrl}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-white">{event.title}</h3>
          <div className="flex items-center gap-2">
            {isAttending && (
              <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">Attending</span>
            )}
            {/* Admin delete button */}
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onDelete(event.event_id); }}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{event.description}</p>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-gray-300">
            <Calendar className="w-4 h-4 text-purple-400" />
            <span>{eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-300">
            <Clock className="w-4 h-4 text-purple-400" />
            <span>{eventDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-300">
            <MapPin className="w-4 h-4 text-purple-400" />
            <span>{event.location}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-300">
            <Users className="w-4 h-4 text-purple-400" />
            <span>
              {event.attendees?.length || 0} attending
              {event.max_attendees && ` / ${event.max_attendees} max`}
            </span>
          </div>
        </div>

        {event.artists?.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">Featured Artists:</p>
            <div className="flex flex-wrap gap-2">
              {event.artists.map((artist, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400">
                  <Music className="w-3 h-3 inline mr-1" />
                  {artist.bio?.substring(0, 20) || 'Artist'}
                </span>
              ))}
            </div>
          </div>
        )}

        {showRSVP && (
          isAttending ? (
            <Button
              onClick={() => onAttend(event.event_id)}
              variant="outline"
              className="w-full border-green-500/30 text-green-400 hover:bg-green-500/10"
              data-testid={`unrsvp-${event.event_id}`}
            >
              <Check className="w-4 h-4 mr-2" /> Attending &mdash; Tap to cancel
            </Button>
          ) : (
            <Button
              onClick={() => onAttend(event.event_id)}
              disabled={isFull}
              className="w-full"
              data-testid={`rsvp-${event.event_id}`}
            >
              {isFull ? 'Event Full' : 'RSVP Now'}
            </Button>
          )
        )}
      </div>
    </div>
  );
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState('discover');
  const { user, isAuthenticated } = useAuthStore();
  const [uploading, setUploading] = useState(false);
  const flyerInputRef = useRef(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    is_public: true,
    max_attendees: '',
    flyer_url: ''
  });

  const [hasArtistProfile, setHasArtistProfile] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isOwner = user?.role === 'owner';
  const isOverseer = user?.role === 'overseer';
  const isArtist = user?.role === 'artist' || hasArtistProfile;
  const canCreateEvent = isAdmin || isOwner || isOverseer || isArtist;

  const checkArtistProfile = useCallback(async () => {
    try {
      const response = await api.get('/artist/profile');
      if (response.data && response.data.status === 'approved') {
        setHasArtistProfile(true);
      }
    } catch (error) {
      // User doesn't have an artist profile
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyEvents = useCallback(async () => {
    try {
      const response = await api.get('/my-events');
      setMyEvents(response.data);
    } catch (error) {
      console.log('Failed to fetch my events');
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      const response = await api.get('/tickets');
      setTickets(response.data || []);
    } catch (error) {
      console.log('Failed to fetch tickets');
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    if (isAuthenticated) {
      fetchMyEvents();
      fetchTickets();
      checkArtistProfile();
    }
  }, [isAuthenticated, fetchEvents, fetchMyEvents, fetchTickets, checkArtistProfile]);

  const handleFlyerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large. Max 10MB.');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'events');
      
      const response = await api.post('/upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewEvent(prev => ({ ...prev, flyer_url: response.data.url }));
      toast.success('Flyer uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload flyer: ' + (error.response?.data?.detail || 'Server error'));
    } finally {
      setUploading(false);
    }
  };

  const removeFlyer = () => {
    setNewEvent(prev => ({ ...prev, flyer_url: '' }));
  };

  const createEvent = async () => {
    if (!newEvent.title || !newEvent.event_date || !newEvent.event_time || !newEvent.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await api.post('/events', {
        title: newEvent.title,
        description: newEvent.description,
        event_date: newEvent.event_date,
        event_time: newEvent.event_time,
        location: newEvent.location,
        is_public: newEvent.is_public,
        ...(newEvent.max_attendees && { max_attendees: parseInt(newEvent.max_attendees) }),
        ...(newEvent.flyer_url && { flyer_url: newEvent.flyer_url })
      });
      toast.success('Event created successfully!');
      setShowCreate(false);
      setNewEvent({ title: '', description: '', event_date: '', event_time: '', location: '', is_public: true, max_attendees: '', flyer_url: '' });
      fetchEvents();
      fetchMyEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      toast.success('Event deleted successfully');
      fetchEvents();
      fetchMyEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const [rsvpModal, setRsvpModal] = useState(null);
  const [rsvpFirst, setRsvpFirst] = useState('');
  const [rsvpInitial, setRsvpInitial] = useState('');
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [issuedTicket, setIssuedTicket] = useState(null);

  const attendEvent = async (eventId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to RSVP');
      return;
    }
    const event = events.find((e) => e.event_id === eventId)
      || myEvents.find((e) => e.event_id === eventId);
    const isAttending = event?.attendees?.includes(user?.user_id);
    if (isAttending) {
      // Cancellation path — no body needed
      try {
        await api.post(`/events/${eventId}/attend`);
        toast.success('RSVP cancelled');
        fetchEvents();
        fetchMyEvents();
        fetchTickets();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update RSVP');
      }
      return;
    }
    // New RSVP — open modal to collect attendee name
    setRsvpModal(event);
    setRsvpFirst(user?.name?.split(' ')[0] || '');
    setRsvpInitial((user?.name?.split(' ').slice(-1)[0] || '').slice(0, 1).toUpperCase());
  };

  const submitRsvp = async () => {
    if (!rsvpFirst.trim() || !rsvpInitial.trim()) {
      toast.error('Please enter your first name and last initial');
      return;
    }
    setRsvpSubmitting(true);
    try {
      const res = await api.post(`/events/${rsvpModal.event_id}/attend`, {
        first_name: rsvpFirst.trim(),
        last_initial: rsvpInitial.trim().slice(0, 1).toUpperCase(),
      });
      toast.success('RSVP confirmed — ticket issued!');
      setIssuedTicket(res.data?.ticket || null);
      setRsvpModal(null);
      fetchEvents();
      fetchMyEvents();
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to RSVP');
    } finally {
      setRsvpSubmitting(false);
    }
  };

  const downloadTicket = async (ticket) => {
    if (!ticket?.qr_data_url) return;
    try {
      const blob = await (await fetch(ticket.qr_data_url)).blob();
      const file = new File([blob], `ticket-${ticket.ticket_id}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Event Ticket QR' });
        toast.success('Tap "Save Image" to add the QR to your Photos');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticket.ticket_id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('QR code saved');
    } catch (err) {
      if (err?.name === 'AbortError') return;
      toast.info('Press and hold the QR image, then choose "Save Image" / "Add to Photos"');
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Events</h1>
            <p className="text-gray-400">Discover live music events and performances</p>
          </div>
          {canCreateEvent && (
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          )}
        </div>

        {/* Admin Notice */}
        {isAdmin && (
          <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mb-6">
            <p className="text-purple-300 text-sm">
              <strong>Admin Mode:</strong> You can delete any event by clicking the trash icon.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          <Button
            variant={activeTab === 'discover' ? 'default' : 'outline'}
            onClick={() => setActiveTab('discover')}
          >
            Discover
          </Button>
          {isAuthenticated && (
            <Button
              variant={activeTab === 'my-events' ? 'default' : 'outline'}
              onClick={() => setActiveTab('my-events')}
            >
              My Events ({myEvents.length})
            </Button>
          )}
          {isAuthenticated && (
            <Button
              variant={activeTab === 'tickets' ? 'default' : 'outline'}
              onClick={() => setActiveTab('tickets')}
              data-testid="tab-my-tickets"
            >
              <Ticket className="w-4 h-4 mr-2" />
              My Tickets ({tickets.filter(t => t.status === 'active').length})
            </Button>
          )}
        </div>

        {/* Create Event Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Create Event</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Title *</label>
                  <Input
                    value={newEvent.title}
                    onChange={e => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="Event title"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Describe your event"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Date *</label>
                    <Input
                      type="date"
                      value={newEvent.event_date}
                      onChange={e => setNewEvent({ ...newEvent, event_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Time *</label>
                    <Input
                      type="time"
                      value={newEvent.event_time}
                      onChange={e => setNewEvent({ ...newEvent, event_time: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Location *</label>
                  <Input
                    value={newEvent.location}
                    onChange={e => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="Event location"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Max Attendees (optional)</label>
                  <Input
                    type="number"
                    value={newEvent.max_attendees}
                    onChange={e => setNewEvent({ ...newEvent, max_attendees: e.target.value })}
                    placeholder="Leave empty for unlimited"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newEvent.is_public}
                    onChange={e => setNewEvent({ ...newEvent, is_public: e.target.checked })}
                    className="rounded bg-gray-800 border-gray-700"
                  />
                  <label className="text-sm text-gray-300">Make this event public</label>
                </div>
                
                {/* Flyer Upload Section */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Event Flyer (optional)
                  </label>
                  <input
                    ref={flyerInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFlyerUpload}
                    className="hidden"
                  />
                  
                  {!newEvent.flyer_url ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => flyerInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full border-dashed border-gray-600 hover:border-purple-500"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Flyer Image'}
                    </Button>
                  ) : (
                    <div className="relative">
                      <img
                        src={newEvent.flyer_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${newEvent.flyer_url}` : newEvent.flyer_url}
                        alt="Event flyer"
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <button
                        onClick={removeFlyer}
                        className="absolute top-2 right-2 bg-red-500 rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Supported: JPG, PNG, WebP • Max 10MB
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={createEvent} className="flex-1" disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Create Event'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : activeTab === 'discover' ? (
          events.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl">
              <Calendar className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No upcoming events</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map(event => (
                <EventCard 
                  key={event.event_id} 
                  event={event} 
                  user={user}
                  isAdmin={isAdmin}
                  onDelete={deleteEvent}
                  onAttend={attendEvent}
                />
              ))}
            </div>
          )
        ) : activeTab === 'my-events' ? (
          myEvents.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl">
              <Calendar className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">You have not joined any events yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myEvents.map(event => (
                <EventCard 
                  key={event.event_id} 
                  event={event} 
                  showRSVP={false}
                  user={user}
                  isAdmin={isAdmin}
                  onDelete={deleteEvent}
                  onAttend={attendEvent}
                />
              ))}
            </div>
          )
        ) : (
          tickets.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl" data-testid="tickets-empty">
              <Ticket className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">You have no tickets yet — RSVP to an event to get one.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6" data-testid="tickets-grid">
              {tickets.map(ticket => (
                <div
                  key={ticket.ticket_id}
                  className={`rounded-2xl p-5 border ${ticket.status === 'scanned' ? 'bg-gray-800/40 border-gray-700' : 'bg-gradient-to-br from-purple-900/40 to-fuchsia-900/30 border-purple-500/30'}`}
                  data-testid={`ticket-${ticket.ticket_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-purple-300 mb-1">Ticket</p>
                      <h3 className="text-lg font-bold text-white">{ticket.event_title}</h3>
                      <p className="text-sm text-gray-400">{ticket.event_date} · {ticket.event_time}</p>
                      <p className="text-sm text-gray-400">{ticket.event_location}</p>
                    </div>
                    {ticket.status === 'scanned' ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-gray-300">Scanned</span>
                    ) : ticket.status === 'cancelled' ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-300">Cancelled</span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-300">Active</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {ticket.qr_data_url ? (
                      <img
                        src={ticket.qr_data_url}
                        alt="Ticket QR"
                        className="w-32 h-32 rounded-lg bg-black p-1"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-lg bg-black/40 flex items-center justify-center text-gray-500 text-xs">No QR</div>
                    )}
                    <div className="flex-1">
                      <p className="text-white font-semibold">{ticket.attendee_name}</p>
                      <p className="text-xs text-gray-500 mt-1 break-all">ID: {ticket.ticket_id}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                        onClick={() => downloadTicket(ticket)}
                        data-testid={`download-ticket-${ticket.ticket_id}`}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Save QR
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* RSVP Modal — capture first name + last initial */}
        {rsvpModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="rsvp-modal">
            <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-purple-500/30">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-purple-400" />
                  RSVP for {rsvpModal.title}
                </h2>
                <button
                  onClick={() => setRsvpModal(null)}
                  className="text-gray-400 hover:text-white"
                  data-testid="rsvp-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Enter your name as it should appear on your ticket. We use just your first name and last initial.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">First name</label>
                  <Input
                    value={rsvpFirst}
                    onChange={e => setRsvpFirst(e.target.value)}
                    placeholder="Alex"
                    maxLength={50}
                    data-testid="rsvp-first-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Last initial</label>
                  <Input
                    value={rsvpInitial}
                    onChange={e => setRsvpInitial(e.target.value.slice(0, 1).toUpperCase())}
                    placeholder="A"
                    maxLength={1}
                    data-testid="rsvp-last-initial"
                  />
                </div>
                <p className="text-xs text-gray-500 italic">
                  Will appear as: <span className="text-purple-300">{(rsvpFirst || 'Alex').trim().replace(/\b\w/g, c => c.toUpperCase())} {(rsvpInitial || 'A').toUpperCase()}.</span>
                </p>
              </div>
              <div className="flex gap-3 mt-5">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setRsvpModal(null)}
                  disabled={rsvpSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={submitRsvp}
                  disabled={rsvpSubmitting}
                  data-testid="rsvp-submit"
                >
                  {rsvpSubmitting ? 'Issuing…' : 'Get my ticket'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Issued Ticket Success Modal */}
        {issuedTicket && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="ticket-success-modal">
            <div className="bg-gradient-to-br from-purple-900 to-fuchsia-900 rounded-2xl p-6 w-full max-w-md border border-purple-400/40 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Check className="w-6 h-6 text-green-400" />
                  Ticket Confirmed
                </h2>
                <button
                  onClick={() => setIssuedTicket(null)}
                  className="text-gray-300 hover:text-white"
                  data-testid="ticket-success-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-purple-200 mb-4">
                Show this QR code at the door. A copy is also saved to your "My Tickets" tab.
              </p>
              <div className="bg-black rounded-xl p-4 flex flex-col items-center">
                {issuedTicket.qr_data_url && (
                  <img
                    src={issuedTicket.qr_data_url}
                    alt="Ticket QR"
                    className="w-56 h-56 rounded-lg"
                    data-testid="issued-ticket-qr"
                  />
                )}
                <p className="text-white font-bold mt-3">{issuedTicket.attendee_name}</p>
                <p className="text-xs text-gray-400 mt-1">{issuedTicket.event_title}</p>
                <p className="text-[10px] text-gray-500 mt-2 font-mono">{issuedTicket.ticket_id}</p>
              </div>
              <div className="flex gap-3 mt-5">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  onClick={() => downloadTicket(issuedTicket)}
                  data-testid="download-issued-ticket"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Save QR
                </Button>
                <Button
                  className="flex-1 bg-white text-purple-900 hover:bg-purple-100"
                  onClick={() => { setIssuedTicket(null); setActiveTab('tickets'); }}
                >
                  View my tickets
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
